import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Typography,
} from "@suraasa/placebo-ui"
import LinkedInIcon from "assets/icons/linkedin-icon.svg"
import quoteLogo from "assets/logos/quote-logo.png"
import clsx from "clsx"
import { NavArrowDown } from "iconoir-react"
import React, { useState } from "react"

import {
  CareerGrowth,
  ImprovementList,
  TeachAbroad,
  TeacherProfile,
} from "./data"
import Image from "./Image"

interface QuoteBoxProps {
  teacherName: string
  quote: string
  videoLink: string
  linkedinLink: string | null
}

const QuoteBox: React.FC<QuoteBoxProps> = ({
  teacherName,
  quote,
  videoLink,
  linkedinLink,
}) => {
  const videoPlayer = (
    <iframe
      src={videoLink.replace("youtu.be/", "youtube.com/embed/")}
      title="YouTube video player"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      allowFullScreen
      className="h-[232px] w-full rounded-2xl"
    ></iframe>
  )
  const quoteText = (
    <div className="flex h-full flex-col text-white">
      <Typography variant="largeBody" className="grow">
        {quote}
      </Typography>
      <a
        className={clsx("mt-2", { "hover:underline": linkedinLink })}
        href={linkedinLink || ""}
        target="_blank"
        rel="noopener noreferrer"
      >
        <div className="flex flex-row gap-0.5">
          <Typography variant="title3">
            <strong>{teacherName}</strong>, Suraasa Teacher
          </Typography>
          <div className="w-0.5" />
          {linkedinLink !== null && (
            <a>
              <img src={LinkedInIcon} alt="LinkedIn" />
            </a>
          )}
        </div>
      </a>
    </div>
  )
  return (
    <div
      style={{
        background: `linear-gradient(96.11deg, #282D52 0.66%, #44354C 100%)`,
      }}
      className="relative rounded-2xl p-2 sm:mx-6 sm:p-5"
    >
      <div className="hidden sm:block">
        <img src={quoteLogo} alt="" className="absolute opacity-50" />
        <div
          className="grid grid-cols-2 gap-2"
          style={{ gridTemplateColumns: "60% 40%" }}
        >
          <div className="flex flex-col justify-between">{quoteText}</div>
          <div className="pr-1">{videoPlayer}</div>
        </div>
      </div>
      <div className="flex flex-col sm:hidden">
        <img src={quoteLogo} alt="" className="absolute" />
        {quoteText}
        <div className="mt-2 pr-1">{videoPlayer}</div>
      </div>
    </div>
  )
}

interface ProfileCardProps {
  name: string
  subject: string | null
  experience: string
  qualifications: string | null
  homeCountry: string
  targetCountry: string | null
}

const ProfileCard: React.FC<ProfileCardProps> = ({
  name,
  subject,
  experience,
  qualifications,
  homeCountry,
  targetCountry,
}) => {
  return (
    <div className="flex items-center rounded-lg border-2 border-onSurface-200 bg-white p-4 sm:space-x-6">
      <div className="grid grid-cols-2 gap-x-10 gap-y-3 sm:grid-cols-3 sm:gap-y-6">
        <div className="col-span-1 gap-0.5">
          <Typography variant="preTitle" className="text-onSurface-600">
            Name
          </Typography>
          <Typography variant="title4">{name}</Typography>
        </div>
        {subject && (
          <div className="col-span-1 gap-0.5">
            <Typography variant="preTitle" className="text-onSurface-600">
              Subject
            </Typography>
            <Typography variant="title4">{subject}</Typography>
          </div>
        )}
        <div className="col-span-1 gap-0.5">
          <Typography variant="preTitle" className="text-onSurface-600">
            Experience
          </Typography>
          <Typography variant="title4">{experience}</Typography>
        </div>

        {qualifications && (
          <div className="col-span-1 gap-0.5">
            <Typography variant="preTitle" className="text-onSurface-600">
              Qualifications
            </Typography>
            <Typography variant="title4">{qualifications}</Typography>
          </div>
        )}
        <div className="col-span-1 gap-0.5">
          <Typography variant="preTitle" className="text-onSurface-600">
            Home Country
          </Typography>
          <Typography variant="title4">{homeCountry}</Typography>
        </div>
        {targetCountry && (
          <div className="col-span-1 gap-0.5">
            <Typography variant="preTitle" className="text-onSurface-600">
              Target Country
            </Typography>
            <Typography variant="title4">{targetCountry}</Typography>
          </div>
        )}
      </div>
    </div>
  )
}

const CaseStudy: React.FC = () => {
  const [selectedTeacher, setSelectedTeacher] = useState<TeacherProfile>(
    TeachAbroad[0]
  )

  const handleTeacherSelect = (teacher: any) => {
    setSelectedTeacher(teacher)
  }

  const DropDownMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex h-fit flex-row items-center gap-1">
          <Typography variant="body" className="text-onSurface-800">
            {selectedTeacher.name}
          </Typography>
          <NavArrowDown />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuGroup>
          {[...TeachAbroad, ...CareerGrowth, ...ImprovementList]
            .sort((a, b) => a.name.localeCompare(b.name)) // Correct alphabetical sorting
            .map((teacher: TeacherProfile) => (
              <DropdownMenuItem
                key={teacher.name}
                onClick={() => handleTeacherSelect(teacher)}
              >
                {teacher.name}
              </DropdownMenuItem>
            ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )

  return (
    <div>
      <div className="flex flex-row flex-wrap justify-between">
        <Typography variant="title1" className="text-onSurface-800">
          {selectedTeacher.name}&apos;s Case Study
        </Typography>
        {DropDownMenu}
      </div>

      <div className="relative w-full sm:mt-4">
        {/* <div className="absolute -top-0 size-[200px] overflow-hidden rounded-full sm:block">
          <img
            src={SuraasaIcon}
            alt=""
            className="size-full bg-white object-cover"
          />
        </div> */}
        <ProfileCard
          name={selectedTeacher.name}
          subject={selectedTeacher.subject}
          experience={selectedTeacher.experience}
          qualifications={selectedTeacher.qualifications}
          homeCountry={selectedTeacher.currentLocation}
          targetCountry={selectedTeacher.targetCountry}
        />
      </div>

      <div className="h-6"></div>

      <Image src={selectedTeacher.pathImage} />
      <div className="h-5"></div>
      <QuoteBox
        teacherName={selectedTeacher.name}
        quote={selectedTeacher.quote}
        videoLink={selectedTeacher.videoLink}
        linkedinLink={selectedTeacher.linkedInLink}
      />
      <div className="h-5"></div>
    </div>
  )
}

export default CaseStudy
